import axios from 'axios';
import { Cookies } from 'react-cookie';
import { toast } from 'react-toastify';
import { store } from '../store';
import {
  setSelectedMerchant,
  clearMerchantState,
  selectMerchantId,
  selectMerchants,
} from '../store/slices/merchantSlice';
import type { RootState } from '../types';

const cookies = new Cookies();

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/** Delay (ms) before redirecting to login after 403 when user has no merchants, so the toast is visible. */
const LOGIN_REDIRECT_DELAY_MS = 3000;

if (!API_BASE_URL) {
  throw new Error('API_BASE_URL is not defined in the .env file');
}

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

// Request Interceptor – attach auth and X-Merchant-ID
axiosInstance.interceptors.request.use(
  (config) => {
    const authToken = cookies.get('authToken');
    if (authToken) {
      config.headers['Authorization'] = `Bearer ${authToken}`;
    }
    const merchantId = selectMerchantId(store.getState() as RootState);
    if (merchantId) {
      config.headers['X-Merchant-ID'] = merchantId;
    }
    config.headers['Content-Type'] = 'application/json';
    config.headers['Accept'] = 'application/json';
    return config;
  },
  (error) => Promise.reject(error),
);

// Response Interceptor – 401 refresh + 403 merchant-switch (aligned with api/index behaviour)
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        const refreshToken = cookies.get('refreshToken');
        const newAuthToken = await axiosInstance.patch('user/staff/login', {
          refresh_token: refreshToken,
        });

        cookies.set('authToken', newAuthToken.data.data.access_token, {
          path: '/',
          secure: true, // Use HTTPS for secure environments
          sameSite: 'strict',
        });

        originalRequest.headers['Authorization'] =
          `Bearer ${newAuthToken.data.data.access_token}`;

        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error('Token refresh failed, logging out...');
        cookies.remove('authToken', { path: '/' });
        cookies.remove('refreshToken', { path: '/' });
        cookies.remove('isAuthenticated', { path: '/' });
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // 403: switch to first merchant, or logout if no merchants (same as api/index)
    // Skip when the failed request is the login endpoint – let Login page show the API message
    const isLoginRequest = originalRequest?.url?.includes('user/staff/login');
    if (error.response?.status === 403 && !isLoginRequest) {
      const merchants = selectMerchants(store.getState() as RootState);
      if (merchants.length > 0) {
        store.dispatch(setSelectedMerchant(merchants[0]));
      } else {
        toast.error(
          'You do not have access to any merchant. Please contact your administrator.',
          { autoClose: 5000 },
        );
        store.dispatch(clearMerchantState());
        cookies.remove('authToken', { path: '/' });
        cookies.remove('refreshToken', { path: '/' });
        cookies.remove('isAuthenticated', { path: '/' });
        // Delay redirect so the toast is visible before full-page navigation
        setTimeout(() => {
          window.location.href = '/login';
        }, LOGIN_REDIRECT_DELAY_MS);
      }
    }

    return Promise.reject(error);
  },
);

// API Service Methods
const ApiService = {
  get: async (url: string, params?: unknown) => {
    try {
      const response = await axiosInstance.get(url, { params });
      return response.data;
    } catch (error) {
      console.error(`GET request failed: ${error}`);
      throw error;
    }
  },
  post: async (url: string, data: unknown) => {
    try {
      const response = await axiosInstance.post(url, data);
      return response.data;
    } catch (error) {
      console.error(`POST request failed: ${error}`);
      throw error;
    }
  },
  patch: async (url: string, data: unknown) => {
    try {
      const response = await axiosInstance.patch(url, data);
      return response.data;
    } catch (error) {
      console.error(`PATCH request failed: ${error}`);
      throw error;
    }
  },
  put: async (url: string, data: unknown) => {
    try {
      const response = await axiosInstance.put(url, data);
      return response.data;
    } catch (error) {
      console.error(`PUT request failed: ${error}`);
      throw error;
    }
  },
  delete: async (url: string) => {
    try {
      const response = await axiosInstance.delete(url);
      return response.data;
    } catch (error) {
      console.error(`DELETE request failed: ${error}`);
      throw error;
    }
  },
  downloadFileBuffer: async (
    url: string,
    format: string,
    filename: string,
    body?: unknown,
    // signal: lets the caller cancel the request (closing the download modal). Cancelling drops
    // the connection; the transactions export stops paging when it sees that once #1253 is
    // deployed (before that, and for the other export endpoints, the server finishes the file).
    options: { signal?: AbortSignal } = {},
  ) => {
    try {
      const response = await axiosInstance.post(url, body, {
        responseType: 'arraybuffer', // Treat response as binary
        timeout: 300000, // 5 minutes custom timeout
        signal: options.signal,
      });

      // Create a blob from the array buffer
      const blob = new Blob([response.data], { type: format });

      // Generate a download link
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename); // Use the provided filename
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error(`File download failed: ${error}`);
      throw error;
    }
  },
};

export default ApiService;
